import { useState } from "react";
import { Empty, Spin, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import ActiveGauge from "./ActiveGauge";

const PANEL_TABS = [
  { key: "status", label: "Status" },
  { key: "onboarding", label: "Onboarding" },
];

/**
 * PartnerDetailsPanel — Status gauge + stats, Onboarding document completion,
 * and Top Performers.
 *
 * Status counts still come from GET /riders; Top Performers and Onboarding come
 * from the Dashboard API. Onboarding is deliberately unfiltered — a company-wide,
 * all-time snapshot that must not move when the top filter bar changes.
 */
export default function PartnerDetailsPanel({
  loading = false,
  stats = { total: 0, active: 0, inactive: 0, activePercent: null },
  performers = [],
  performersLoading = false,
  onboarding = { loading: false, totalActive: 0, documents: [] },
}) {
  const [activeTab, setActiveTab] = useState("status");
  const navigate = useNavigate();

  const hasStats = stats.total > 0 || stats.active > 0 || stats.inactive > 0;

  return (
    <div className="dash-partner-panel">
      <div className="dash-partner-panel-title">Partner Details</div>

      <div className="dash-partner-tabs">
        {PANEL_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`dash-partner-tab${activeTab === tab.key ? " active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "status" ? (
        <Spin spinning={loading}>
          <ActiveGauge percent={hasStats ? stats.activePercent : null} />
          <div className="dash-partner-stats">
            <div className="dash-partner-stat">
              <span>
                <span className="dash-dot muted" /> Total Partner
              </span>
              <span className="dash-partner-stat-value">
                {hasStats ? stats.total : "—"}
              </span>
            </div>
            <div className="dash-partner-stat">
              <span>
                <span className="dash-dot green" /> Active Partner
              </span>
              <span className="dash-partner-stat-value">
                {hasStats ? `${stats.active}/${stats.total || 0}` : "—"}
              </span>
            </div>
            <div className="dash-partner-stat">
              <span>
                <span className="dash-dot light" /> In-active Partner
              </span>
              <span className="dash-partner-stat-value">
                {hasStats ? `${stats.inactive}/${stats.total || 0}` : "—"}
              </span>
            </div>
          </div>
        </Spin>
      ) : (
        <Spin spinning={onboarding.loading}>
          <div className="dash-onboarding">
            <div className="dash-onboarding-note">
              Approved documents across all active partners — not affected by the
              filters above.
            </div>
            {onboarding.documents.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={onboarding.loading ? "Loading…" : "No documents"}
              />
            ) : (
              onboarding.documents.map((doc) => {
                const total = doc.totalActive || 0;
                const percent = total
                  ? Math.round((doc.approvedCount / total) * 100)
                  : 0;
                return (
                  <div className="dash-onboarding-row" key={doc.documentType}>
                    <div className="dash-onboarding-head">
                      <span className="dash-onboarding-label">{doc.label}</span>
                      <span className="dash-onboarding-fraction">
                        {doc.approvedCount}/{total}
                      </span>
                    </div>
                    <div className="dash-onboarding-bar">
                      <div
                        className="dash-onboarding-bar-fill"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Spin>
      )}

      <div className="dash-top-performers">
        <div className="dash-top-performers-header">
          <span className="dash-top-performers-title">Top Performers</span>
          <button
            type="button"
            className="dash-top-performers-link"
            onClick={() => navigate("/delivery-partners")}
          >
            View All
          </button>
        </div>

        <Spin spinning={performersLoading}>
          {performers.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No performers yet"
            />
          ) : (
            performers.map((p) => (
              <div className="dash-performer-row" key={p.id}>
                <div className="dash-performer-avatar">{p.initials}</div>
                <div className="dash-performer-info">
                  <div className="dash-performer-name">
                    {p.name}
                    {p.isDeleted && (
                      <Tag color="red" className="dash-performer-tag">
                        DELETED
                      </Tag>
                    )}
                    {!p.isDeleted && p.status === "inactive" && (
                      <Tag className="dash-performer-tag">INACTIVE</Tag>
                    )}
                  </div>
                  <div className="dash-performer-id">FID: {p.riderId}</div>
                </div>
                <div className="dash-performer-stats">
                  <div className="dash-performer-orders">{p.orders}</div>
                  <div className="dash-performer-orders-label">ORDERS</div>
                </div>
              </div>
            ))
          )}
        </Spin>
      </div>
    </div>
  );
}
